import { createRoot } from 'react-dom/client';
import { applyMiddleware, createStore } from 'redux';
import { Provider } from 'react-redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import { createLogger } from 'redux-logger';
import { thunk } from 'redux-thunk';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import prototypes from 'dashboard-services/prototypes';

import { initialState, ngApp } from 'reducers';
import properties from 'resources/constants/properties.json';

import App from 'components/App';

import ErrorBoundaryLocal from './components/App/ErrorBoundary';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import 'core-js/stable';

// plugins has to be added/removed also in HtmlEditorConst.js
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/skins/ui/oxide/skin';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/code';
import 'tinymce/plugins/image';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/media';
import 'tinymce/plugins/pagebreak';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/skins/content/default/content';
import 'tinymce/skins/ui/oxide/content';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/help';
import 'tinymce/plugins/help/js/i18n/keynav/en';
import 'tinymce/plugins/fullscreen';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import './index.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);

if("".capitaliseFirstLetter === undefined) {
  prototypes.init()
}

const fav = document.createElement("link")
fav.setAttribute("href", properties.faviconUrl)
fav.setAttribute("type", "image/ico")
fav.setAttribute("rel", "icon")
const title = document.createElement("title")
title.innerHTML = properties.title
document.getElementsByTagName("head")[0].appendChild(fav)
document.getElementsByTagName("head")[0].appendChild(title)

const devTools = () => {
  const loggerMiddleware = createLogger({ diff: false, predicate: (getState, action) => action?.type !==  "HANDLE_REQUEST_GLOBAL" })
  return composeWithDevTools(
    applyMiddleware(
      thunk,
      loggerMiddleware
    )
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: "online",
      retry: 0,
      retryDelay: 3000,
      staleTime: 3000
    },
    mutations: {
      networkMode: "online"
    }
  }
})
const router = createBrowserRouter([
  {
    path: '*',
    element: <App/>,
    ErrorBoundary: ErrorBoundaryLocal
  }
]);
const root = createRoot(document.getElementById("app"));
const renderApp = () => root.render(
  <Provider store={window.store}>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router}/>
      {properties.version === "LOCALHOST" && (
        <ReactQueryDevtools 
            buttonPosition="top-right"
            initialIsOpen={false} 
            position="right"
        />
      )}
    </QueryClientProvider>
  </Provider>
)

const dev = () => {
  if (!window.store) {
    window.store = createStore((state, action) => ngApp(state, action), initialState, devTools())
  } else {
    window.store.replaceReducer((state, action) => ngApp(state, action), initialState, devTools());
  }
  renderApp();
}

const prod = () => {
  window.store = createStore((state, action) => ngApp(state, action), initialState, applyMiddleware(thunk))
  renderApp();
}

process.env.NODE_ENV !== 'production' ? dev() : prod();
require.context(
  'file-loader?name=[path][name].[ext]&context=node_modules/tinymce!tinymce/skins',
  true,
  /.*/
);